import { useCallback, useEffect, useRef, useState } from 'react';
import AgentSocket from '../api/agentSocket';

let messageIdCounter = 0;
function nextMessageId() {
  messageIdCounter += 1;
  return `msg_${messageIdCounter}`;
}

/**
 * React hook driving one conversational session with the LangGraph agent
 * over WebSocket. See api/agentSocket.js for the transport and
 * backend/langgraph_agents/protocol.py for the wire protocol this mirrors.
 *
 * Returns a chat-oriented view of the connection: a running transcript
 * (`messages`), the current progress label while a turn is in flight
 * (`thinkingStep`), any pending Human-in-the-Loop request (`pendingHitl`),
 * and the actions to drive it (`sendMessage`, `resolveHitl`).
 */
export default function useAgentChat({ threadId: initialThreadId = null } = {}) {
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [threadId, setThreadId] = useState(initialThreadId);
  const [messages, setMessages] = useState([]);
  const [thinkingStep, setThinkingStep] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [pendingHitl, setPendingHitl] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const socketRef = useRef(null);
  const lastTokenRef = useRef(null);
  const gracefulDisconnectTimeoutRef = useRef(null);

  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'session_ready':
        setThreadId(event.data.thread_id);
        if (socketRef.current) {
          socketRef.current.threadId = event.data.thread_id;
        }
        break;

      case 'ack':
        setIsBusy(true);
        setActivityLog([]);
        break;

      case 'thinking_step':
        setThinkingStep(event.data.message);
        setActivityLog((prev) => {
          const detail = typeof event.data.detail === 'string' ? event.data.detail.trim() : '';
          const combinedText = detail ? `${event.data.message}\n${detail}` : event.data.message;
          const nextEntry = {
            id: nextMessageId(),
            text: combinedText,
            createdAt: new Date().toISOString(),
          };
          return [...prev, nextEntry].slice(-8);
        });
        break;

      case 'hitl_required':
        setIsBusy(false);
        setThinkingStep(null);
        setActivityLog([]);
        setPendingHitl({
          checkpointId: event.data.checkpoint_id,
          turnId: event.data.turn_id,
          request: event.data.request,
        });

        // Always append a new HITL prompt to the chat transcript.
        // Repeated ambiguities must remain actionable at the bottom of the chat,
        // even if the same question/kind already appeared earlier in the turn.
        setMessages((prev) => [...prev, {
          id: nextMessageId(),
          role: 'hitl',
          turnId: event.data.turn_id,
          checkpointId: event.data.checkpoint_id,
          request: event.data.request,
          decision: undefined,
        }]);
        break;

      case 'final_answer':
        setIsBusy(false);
        setThinkingStep(null);
        setActivityLog([]);
        setPendingHitl(null);
        setMessages((prev) => [...prev, {
          id: nextMessageId(),
          role: 'assistant',
          turnId: event.data.turn_id,
          status: event.data.status,
          answer: event.data.answer
        }]);
        break;

      case 'error':
        setIsBusy(false);
        setThinkingStep(null);
        setActivityLog([]);
        setMessages((prev) => [...prev, {
          id: nextMessageId(),
          role: 'error',
          code: event.data.code,
          message: event.data.message
        }]);
        break;

      case 'pong':
      default:
        break;
    }
  }, []);

  useEffect(() => {
    const socket = new AgentSocket({
      threadId: initialThreadId,
      onOpen: () => setConnectionStatus('open'),
      onClose: () => setConnectionStatus('closed'),
      onEvent: handleEvent
    });
    socketRef.current = socket;
    setConnectionStatus('connecting');
    socket.connect();

    return () => {
      // In React StrictMode, useEffect cleanup runs immediately after mount to detect bugs.
      // This causes the socket to close while connecting. Use a grace period to detect
      // if we're about to remount (common in StrictMode). Only disconnect if we don't
      // remount within 50ms.
      if (gracefulDisconnectTimeoutRef.current) {
        clearTimeout(gracefulDisconnectTimeoutRef.current);
      }
      
      gracefulDisconnectTimeoutRef.current = setTimeout(() => {
        console.log('[useAgentChat] Unmounting permanently. Disconnecting socket.');
        socket.disconnect();
        gracefulDisconnectTimeoutRef.current = null;
      }, 50);
    };
    // Intentionally connect once per mount; reconnects are handled internally by AgentSocket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Monitor token changes and reconnect if token becomes available
  useEffect(() => {
    const checkTokenInterval = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      const previousToken = lastTokenRef.current;

      // If token appears after being missing, try to reconnect
      if (currentToken && !previousToken && socketRef.current && connectionStatus === 'closed') {
        console.log('[useAgentChat] Token recovered. Attempting reconnection...');
        lastTokenRef.current = currentToken;
        socketRef.current.connect();
      } else {
        lastTokenRef.current = currentToken;
      }
    }, 1000);

    return () => clearInterval(checkTokenInterval);
  }, [connectionStatus]);

  // Cleanup graceful disconnect timeout on component unmount
  useEffect(() => {
    return () => {
      if (gracefulDisconnectTimeoutRef.current) {
        clearTimeout(gracefulDisconnectTimeoutRef.current);
        gracefulDisconnectTimeoutRef.current = null;
      }
    };
  }, []);

  const sendMessage = useCallback((text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || !socketRef.current) return;
    setMessages((prev) => [...prev, { id: nextMessageId(), role: 'user', text: trimmed }]);
    setIsBusy(true);
    socketRef.current.sendUserMessage(trimmed);
  }, []);

  const resolveHitl = useCallback((decision) => {
    if (!pendingHitl || !socketRef.current) return;
    const checkpointId = pendingHitl.checkpointId || pendingHitl.checkpoint_id;
    if (!checkpointId) {
      setIsBusy(false);
      setMessages((prev) => [...prev, {
        id: nextMessageId(),
        role: 'error',
        code: 'missing_checkpoint_id',
        message: 'No se pudo resolver HITL: falta checkpoint_id en el estado local.'
      }]);
      return;
    }
    
    // Store the decision in the pending HITL message BEFORE sending to server
    // This ensures the response is visible immediately
    setMessages((prev) => {
      // Resolve the most recent matching HITL prompt in case the same
      // checkpoint/request has been surfaced multiple times in the chat.
      let hitlIndex = -1;
      for (let index = prev.length - 1; index >= 0; index -= 1) {
        const msg = prev[index];
        if (
          msg.role === 'hitl' &&
          (msg.checkpointId === checkpointId || msg.checkpoint_id === checkpointId)
        ) {
          hitlIndex = index;
          break;
        }
      }
      
      if (hitlIndex >= 0) {
        const updated = [...prev];
        updated[hitlIndex] = {
          ...updated[hitlIndex],
          decision
        };
        console.log('✅ Decision stored in HITL message at index', hitlIndex, ':', decision);
        return updated;
      }
      console.warn('⚠️ No HITL message found with checkpointId', checkpointId);
      return prev;
    });
    
    // Now send the decision to the server
    setIsBusy(true);
    socketRef.current.sendHitlDecision(checkpointId, decision);
  }, [pendingHitl]);

  return {
    connectionStatus,
    threadId,
    messages,
    thinkingStep,
    activityLog,
    pendingHitl,
    isBusy,
    sendMessage,
    resolveHitl
  };
}
